import React, { useEffect, useState } from 'react';
import { reset } from 'redux-form';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import _ from 'lodash';
import { Button, Col, Container, Row, Card, CardBody } from 'reactstrap';
import DeleteIcon from 'mdi-react/DeleteIcon';
import SettingsIcon from 'mdi-react/SettingsIcon';
import PencilIcon from 'mdi-react/PencilIcon';
import DataReactTable from '../../../../Tables/DataTable/components/DataReactTable';
import Loading from '../../../../../shared/components/Loading';
import { FullWideNotification } from '../../../../../shared/components/Notification';
import { columns } from './environmentsDataColumns';
import FormAddNewEnvironment from './FormAddNewEnvironment/FormAddNewEnvironment';
import FormEditEnvironment from './FormEditEnvironments';
import ConfirmModal from './Variables/ConfirmModal/ConfirmModal';
import {
  getEnvironmentsFetchingAction,
  postEnvironmentsFail,
  postEnvironmentsFetchingAction,
  deleteEnvironmentAction,
  editEnvironmentAction,
} from '../../../../../redux/actions/environmentsActions';
import CustomAlert from '../../../../../shared/components/customComponents/CustomAlert/CustomAlert';
import ReactTooltip from 'react-tooltip';
import roles from '../../../../../config/roles';


const Environments = () => {
  const { id } = useParams();
  const { t } = useTranslation('common');
  const { environments, totalRecords, loading, errors, modalLoading, modalErrors } =
    useSelector((state) => state.environments);
  const authUser = useSelector(state => state.auth.user);
  const dispatch = useDispatch();
  const [modal, setModal] = useState(false);
  const [deletedId, setDeletedId] = useState('');
  const [editEnv, setEditEnv] = useState('');
  const [showNotification, setShowNotification] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(10);
  const [refreshCounter, setRefreshCounter] = useState(0);

  const toggle = () => {
    setModal((prevState) => !prevState);
    dispatch(postEnvironmentsFail([]));
    dispatch(reset('add_new_environment'));
  };

  const toggleDelete = (val = '') => {
    setDeletedId(val);
  };

  const toggleEdit = (value) => {
    dispatch(postEnvironmentsFail([]));

    if (!_.isEmpty(value)) {
      return setEditEnv(value);
    }

    setEditEnv({});

    return '';
  };

  const handleSubmit = (value) => {
    dispatch(
      postEnvironmentsFetchingAction(
        id,
        value.branch,
        value.roles.split(/[\s,]/).filter((s) => s.length),
        () => {
          toggle();
          setRefreshCounter((prevState) => prevState + 1);
        },
      ),
    );
  };

  const handleDelete = () => {
    dispatch(deleteEnvironmentAction(id, deletedId, () => {
      toggleDelete();
      setRefreshCounter((prevState) => prevState + 1);
    }));
  };

  const handleEdit = (value) => {
    dispatch(
      editEnvironmentAction(
        id,
        value.id,
        value.branch,
        value.access_roles.split(/[\s,]/).filter((s) => s.length),
        () => {
          toggleEdit();
          setRefreshCounter((prevState) => prevState + 1);
        },
      ),
    );
  };

  const handleChangePage = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleChangePageSize = (newSize) => {
    setCurrentPage(1);
    setPageLimit(newSize);
  };

  useEffect(() => {
    const query = {
      start: (currentPage - 1) * pageLimit,
      limit: pageLimit
    };

    if (authUser.role === roles.ADMIN || authUser.role === roles.SALES || authUser.role === roles.PM) {
      dispatch(getEnvironmentsFetchingAction(id, authUser.accessToken, query));
    } else {
      setShowNotification(true);
      setTimeout(() => {
        setShowNotification(false);
      }, 7000);
    }
  }, [authUser.role, authUser.accessToken, currentPage, pageLimit, dispatch, id, refreshCounter]);

  const prevNavigationQuery = location.state?.prevQuery ? [...location.state.prevQuery] : [];
    prevNavigationQuery.push({
        query: location.search,
        path: location.pathname,
  });

  const renderContent = () => {
    if (loading) {
      return <Loading />;
    }

    if (errors.length > 0) {
      return (
        <Row>
          <Col sm={12}>
            <Card style={{ marginTop: '20px' }}>
              <CardBody>
                {errors.map((error, i) => {
                  return (
                    <CustomAlert
                      color="danger"
                      className="alert--bordered"
                      icon
                      key={i}
                    >
                      <p>
                        <span className="bold-text">{error.param}</span>{' '}
                        {error.message}
                      </p>
                    </CustomAlert>
                  );
                })}
              </CardBody>
            </Card>
          </Col>
        </Row>
      );
    }

    return (
      <Row>
        <Col sm={12} style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={toggle} color="primary" style={{ marginBottom: '15px' }}>
            {t('environment.tittle_button_add')}
          </Button>
        </Col>
        <DataReactTable
          tableName={t('environment.title_table')}
          sortTable
          search
          emptyStateMessage={t('empty_states.environments')}
          pagination={{
            onChangePageSize: handleChangePageSize,
            onChangePage: handleChangePage,
            currentPage,
            totalRecords,
            pageLimit
          }}
          tableOverrodeConfig={{
            manualPageSize: [-1]
          }}
          reactTableData={{
            tableHeaderData: columns,
            tableRowsData: environments?.map((c) => {
              return {
                ...c,
                id: `${c.id}`,
                project_name: c.project.project_name,
                project_id: `${c.project.project_id}`,
                actions: (
                  <div>
                    <ReactTooltip id="editIcon" type="dark">
                      <span style={{ color: 'white' }}>
                        {t("reactTooltips.edit")}
                      </span>
                    </ReactTooltip>
                    <button data-tip
                      data-for="editIcon"
                      style={{ border: 0, background: 'transparent' }}>
                      <PencilIcon
                        color="#b1c3c8"
                        data-testid="edit-button"
                        size={18}
                        onClick={() => toggleEdit(c)}
                      />
                    </button>
                    <ReactTooltip id="deleteIcon" type="dark">
                      <span style={{ color: 'white' }}>
                        {t("reactTooltips.delete")}
                      </span>
                    </ReactTooltip>
                    <button
                      data-tip
                      data-for="deleteIcon"
                      style={{ border: 0, background: 'transparent' }}>
                      <DeleteIcon
                        color="#b1c3c8"
                        size={18}
                        onClick={() => toggleDelete(c.id)}
                        data-testid="delete-button"
                      />
                    </button>
                    <ReactTooltip id="settingsIcon" type="dark">
                      <span style={{ color: 'white' }}>
                        {t("reactTooltips.variablesSettings")}
                      </span>
                    </ReactTooltip>
                    <button data-tip
                      data-for="settingsIcon"
                      style={{ border: 0, background: 'transparent' }}>
                      <SettingsIcon
                        onClick={() => {
                          navigate(`/management/projects/${id}/edit/environments/${c.branch}/variables`, {
                            state: { prevQuery: prevNavigationQuery, }
                          });
                        }}
                        color="#b1c3c8"
                        size={18}
                      />
                    </button>
                  </div>
                ),
              };
            }),
          }}
        />
        <FormAddNewEnvironment
          modal={modal}
          toggle={toggle}
          onSubmit={handleSubmit}
          errors={modalErrors}
          loading={modalLoading}
        />
        <ConfirmModal
          color="danger"
          btn="Default"
          message="Are you sure you want to delete it?"
          toggle={toggleDelete}
          modal={deletedId}
          onNext={handleDelete}
        />
        {editEnv.id && (
          <FormEditEnvironment
            modal={editEnv}
            toggle={toggleEdit}
            errors={modalErrors}
            loading={modalLoading}
            data={editEnv}
            onSubmit={handleEdit}
          />
        )}
      </Row>
    );
  };

  return (
    <Container>
      {showNotification && (
        <div
          style={{
            position: 'absolute',
            top: '60px',
            left: '0',
            zIndex: '999',
          }}
        >
          <FullWideNotification
            color="danger"
            message="You do not have sufficient permissions to view this page!"
          />
        </div>
      )}
      {renderContent()}
    </Container>
  );
};

export default Environments;
